import { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Download, Mail, Send, RefreshCw, Building2,
} from 'lucide-react';
import { admin, getErrorMessage } from '../../utils/api';

const _money = (n) => `$${Number(n || 0).toFixed(2)}`;

function _download(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
}

export default function AdminReconciliation({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState('monthly');
  const [drlpId, setDrlpId] = useState('');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [building, setBuilding] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await admin.listReconciliation();
      setLogs(res.data.reconciliations || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load reconciliation logs'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const build = async () => {
    try {
      setBuilding(true);
      const payload = { period_type: periodType };
      if (drlpId.trim()) payload.drlp_id = drlpId.trim();
      if (periodType === 'monthly') { payload.year = Number(year); payload.month = Number(month); }
      const res = await admin.buildReconciliation(payload);
      if (res.data.reconciliation) {
        toast.success(`Built reconciliation for ${res.data.reconciliation.period_label}`);
      } else {
        toast.success(`Batch built ${res.data.summary?.built ?? 0} logs`);
      }
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Build failed'));
    } finally {
      setBuilding(false);
    }
  };

  const doDownload = async (log, format) => {
    try {
      const res = await admin.downloadReconciliation(log.id, format);
      _download(res.data, `reconciliation-${log.period_type}-${log.period_label}.${format}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Download failed'));
    }
  };

  const doEmail = async (log) => {
    try {
      const res = await admin.emailReconciliation(log.id);
      toast[res.data.sent ? 'success' : 'error'](res.data.sent ? 'Email sent' : 'Email not sent (check Resend/DRLP email)');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Email failed')); }
  };

  const doPush = async (log) => {
    try {
      const res = await admin.pushReconciliation(log.id);
      toast[res.data.pushed ? 'success' : 'info'](res.data.pushed ? 'Pushed to DRLP API' : `Push skipped: ${res.data.reason || 'no webhook'}`);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Push failed')); }
  };

  const viewDetail = async (id) => {
    try {
      const res = await admin.getReconciliation(id);
      setSelected(res.data.reconciliation);
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to load detail')); }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-reconciliation-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-teal-700" /> Reconciliation Logs
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              Daily &amp; monthly DRLP tax reconciliation (SKU / category / locality level).
              DealShaq remits this tax to Virginia as the registered marketplace facilitator.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="recon-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Build a Reconciliation Log</CardTitle>
            <CardDescription>Leave Retailer ID blank to build for all active DRLPs.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <Label className="text-xs">Period</Label>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger data-testid="recon-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Retailer ID (optional)</Label>
                <Input value={drlpId} onChange={(e) => setDrlpId(e.target.value)} placeholder="all active DRLPs" data-testid="recon-drlp-input" />
              </div>
              {periodType === 'monthly' && (
                <>
                  <div>
                    <Label className="text-xs">Year</Label>
                    <Input value={year} onChange={(e) => setYear(e.target.value)} data-testid="recon-year-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Month</Label>
                    <Input value={month} onChange={(e) => setMonth(e.target.value)} data-testid="recon-month-input" />
                  </div>
                </>
              )}
              <Button onClick={build} disabled={building} data-testid="recon-build-btn" className="bg-teal-700 hover:bg-teal-800">
                {building ? 'Building…' : 'Build'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Generated Logs</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading…</p>
            ) : logs.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="recon-empty">No reconciliation logs yet. Build one above.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Retailer</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Refunds</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`recon-row-${log.id}`}>
                      <TableCell className="font-medium">{log.drlp_name}</TableCell>
                      <TableCell>
                        <button className="text-teal-700 underline" onClick={() => viewDetail(log.id)} data-testid={`recon-view-${log.id}`}>
                          {log.period_type} · {log.period_label}
                        </button>
                      </TableCell>
                      <TableCell className="text-right">{_money(log.summary?.gross_sales)}</TableCell>
                      <TableCell className="text-right">{_money(log.summary?.tax_collected)}</TableCell>
                      <TableCell className="text-right">{_money(log.summary?.refund_total)}</TableCell>
                      <TableCell>
                        {log.delivery?.email_sent_at && <Badge className="bg-emerald-100 text-emerald-800 mr-1">Emailed</Badge>}
                        {log.delivery?.drlp_push_status === 'sent' && <Badge className="bg-blue-100 text-blue-800">Pushed</Badge>}
                        {!log.delivery?.email_sent_at && !log.delivery?.drlp_push_status && <span className="text-xs text-gray-400">—</span>}
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => doDownload(log, 'csv')} data-testid={`recon-csv-${log.id}`}><Download className="w-3 h-3 mr-1" />CSV</Button>
                        <Button size="sm" variant="outline" onClick={() => doDownload(log, 'pdf')} data-testid={`recon-pdf-${log.id}`}><Download className="w-3 h-3 mr-1" />PDF</Button>
                        <Button size="sm" variant="outline" onClick={() => doEmail(log)} data-testid={`recon-email-${log.id}`}><Mail className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" onClick={() => doPush(log)} data-testid={`recon-push-${log.id}`}><Send className="w-3 h-3" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {selected && (
          <Card data-testid="recon-detail-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4" /> {selected.drlp_name} — {selected.period_label}
              </CardTitle>
              <CardDescription>Checksum: {selected.audit_checksum?.slice(0, 30)}… · {selected.period_type}</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-semibold text-sm mb-2">By Tax Category</h4>
                <Table><TableBody>
                  {(selected.category_breakdown || []).map((c) => (
                    <TableRow key={c.category_name}><TableCell>{c.category_name}</TableCell><TableCell className="text-right">{_money(c.sales_in_category)}</TableCell><TableCell className="text-right">{_money(c.tax_collected_in_category)}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-2">By Locality</h4>
                <Table><TableBody>
                  {(selected.locality_breakdown || []).map((l) => (
                    <TableRow key={l.locality_name}><TableCell>{l.locality_name}</TableCell><TableCell className="text-right">{_money(l.sales_in_locality)}</TableCell><TableCell className="text-right">{_money(l.tax_collected_in_locality)}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-2">Transactions ({(selected.transactions || []).length})</h4>
                <Table><TableBody>
                  {(selected.transactions || []).slice(0, 12).map((t, i) => (
                    <TableRow key={i}><TableCell className="text-xs">{t.item_name || t.sku}</TableCell><TableCell className="text-right">×{t.quantity}</TableCell><TableCell className="text-right">{_money(t.tax_amount)}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
